/**
 * The chrome's language (roadmap B9) — the web half of the phone's language row.
 *
 * One module-level dictionary plus a zustand store: components call `useT()` (which subscribes, so
 * a switch re-renders them), plain code calls `t()` directly. The choice is persisted and defaults
 * to the **browser's** language, so an Arabic browser gets the Arabic chrome with nobody flipping
 * a switch.
 *
 * `dir` is set on `<html>`, which is what the layout reads: every spacing utility in this app is
 * logical (`ms-*`, `me-*`, `text-start`), so the flip is the document attribute plus the strings —
 * there is no second stylesheet to keep in sync.
 */
import { create } from 'zustand'
import { en } from './strings.en'
import { ar } from './strings.ar'

export type Locale = 'en' | 'ar'

/** What the switch stores: a locale, or the device's own — the default (the phone's row has the
 * same three). */
export type LocaleChoice = 'system' | Locale

export const LOCALES: { id: LocaleChoice; label: string }[] = [
  { id: 'system', label: 'System' },
  { id: 'en', label: 'English' },
  { id: 'ar', label: 'العربية' },
]

export type MessageKey = keyof typeof en

let dictionary: Record<string, string> = en

/** Look up a key and fill `{name}` placeholders. Unknown keys fall back to English, then the key. */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const template = dictionary[key] ?? en[key] ?? String(key)
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (_, name: string) => String(vars[name] ?? `{${name}}`))
}

/** The hook form: subscribing to the locale is what makes a switch re-render the tree. */
export function useT(): typeof t {
  useLocaleStore((state) => state.locale)
  return t
}

const STORAGE_KEY = 'olmt.locale'

/** What the device asks for — what `system` means. */
function systemLocale(): Locale {
  return navigator.language?.toLowerCase().startsWith('ar') ? 'ar' : 'en'
}

function resolve(choice: LocaleChoice): Locale {
  return choice === 'system' ? systemLocale() : choice
}

function storedChoice(): LocaleChoice {
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === 'system' || stored === 'en' || stored === 'ar' ? stored : 'system'
}

/** The locale the app is showing. Read by `time.ts`, whose `Intl` formatters follow it (B9). */
let active: Locale = 'en'

/** The active locale. Not a hook: the formatters ask outside React, on every call. */
export function currentLocale(): Locale {
  return active
}

/** Applies a locale to the dictionary, the formatters and the document (`lang` + `dir`). */
export function applyLocale(locale: Locale): void {
  active = locale
  dictionary = locale === 'ar' ? ar : en
  document.documentElement.lang = locale
  document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr'
}

type LocaleState = {
  /** What the reader picked; `system` is the default. */
  choice: LocaleChoice
  /** What that resolves to right now — what the chrome actually speaks. */
  locale: Locale
  setLocale: (choice: LocaleChoice) => void
}

export const useLocaleStore = create<LocaleState>()((set) => ({
  choice: 'system',
  locale: 'en',
  setLocale: (choice) => {
    localStorage.setItem(STORAGE_KEY, choice)
    const locale = resolve(choice)
    applyLocale(locale)
    set({ choice, locale })
  },
}))

/** Called once at boot (`main.tsx`), before the first render. */
export function initLocale(): void {
  const choice = storedChoice()
  const locale = resolve(choice)
  applyLocale(locale)
  useLocaleStore.setState({ choice, locale })
}
